import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../BaseComponents/Modal';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Loader';
import { ListBodySystemResponseElement } from '../../../types/apiGeneratedTypes';

export type GenerateProgress = {
  current: number;
  total: number;
  title: string;
  completed: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  bodySystems: ListBodySystemResponseElement[];
  isLoadingList: boolean;
  isGenerating: boolean;
  progress: GenerateProgress | null;
  onGenerate: (bodySystemIds: number[]) => void;
};

const GenerateBodySystemModal: React.FC<Props> = ({
  isOpen,
  onClose,
  bodySystems,
  isLoadingList,
  isGenerating,
  progress,
  onGenerate
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (!isOpen || isGenerating) return;
    setSelectedIds(bodySystems.map((bodySystem) => bodySystem.id));
  }, [isOpen, isGenerating, bodySystems]);

  const allSelected = bodySystems.length > 0 && selectedIds.length === bodySystems.length;

  const selectedTitles = useMemo(() => {
    const selected = new Set(selectedIds);
    return bodySystems.filter((bodySystem) => selected.has(bodySystem.id)).map((item) => item.title);
  }, [bodySystems, selectedIds]);

  const toggleAll = () => {
    if (isGenerating) return;
    setSelectedIds(allSelected ? [] : bodySystems.map((bodySystem) => bodySystem.id));
  };

  const toggleOne = (bodySystemId: number) => {
    if (isGenerating) return;
    setSelectedIds((current) =>
      current.includes(bodySystemId)
        ? current.filter((id) => id !== bodySystemId)
        : [...current, bodySystemId]
    );
  };

  const handleGenerate = () => {
    if (isGenerating || selectedIds.length === 0) return;
    onGenerate(selectedIds);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate score and summary"
      width="520px"
      closeOnEsc={!isGenerating}
    >
      <div className="generate-bs-modal">
        <p className="generate-bs-modal__intro">
          Choose all body systems or a specific set. Score and summary are generated one system at a
          time.
        </p>

        {isLoadingList ? (
          <div className="generate-bs-modal__loading">
            <Loader />
          </div>
        ) : (
          <>
            <label className="generate-bs-modal__select-all">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                disabled={isGenerating || bodySystems.length === 0}
              />
              <span>Select all ({bodySystems.length})</span>
            </label>

            <div className="generate-bs-modal__list">
              {bodySystems.map((bodySystem) => (
                <label key={bodySystem.id} className="generate-bs-modal__item">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(bodySystem.id)}
                    onChange={() => toggleOne(bodySystem.id)}
                    disabled={isGenerating}
                  />
                  <span>{bodySystem.title}</span>
                </label>
              ))}
            </div>
          </>
        )}

        {progress ? (
          <div className="generate-bs-modal__progress">
            <strong>
              {progress.completed}/{progress.total} generated
            </strong>
            <span>
              Generating {progress.current}/{progress.total}: {progress.title}
            </span>
          </div>
        ) : null}

        <div className="generate-bs-modal__actions">
          <Button
            text={isGenerating ? 'Stop remaining' : 'Cancel'}
            onClick={onClose}
            backgroundColor="#ffffff"
            color="#2D334E"
          />
          <Button
            text={
              isGenerating
                ? `${progress?.completed ?? 0}/${progress?.total ?? selectedIds.length} generated`
                : `Generate${selectedIds.length ? ` (${selectedIds.length})` : ''}`
            }
            onClick={handleGenerate}
            isLoading={isGenerating}
            isDisabled={isGenerating || selectedIds.length === 0}
          />
        </div>
        {!isGenerating && selectedTitles.length > 0 && selectedTitles.length < bodySystems.length ? (
          <p className="generate-bs-modal__hint">
            Selected: {selectedTitles.join(', ')}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default GenerateBodySystemModal;
